import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CaretDown, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrandPicker from "./BrandPicker";
import { findConcessionario } from "../config/concessionari";

export default function AgencySwitcher() {
    const { agencyId, selectAgency, user } = useAuth();
    const navigate = useNavigate();
    const [agencies, setAgencies] = useState([]);
    const [open, setOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [form, setForm] = useState({ name: "", address: "", brand_concessionario: null });
    const [saving, setSaving] = useState(false);
    const canCreate = user?.role === "owner";

    const load = () => {
        api.get("/agencies")
            .then((r) => {
                setAgencies(r.data);
                const stillValid = r.data.some((a) => a.id === agencyId);
                if (!stillValid && r.data.length > 0) {
                    selectAgency(r.data[0].id);
                }
            })
            .catch(() => {});
    };

    useEffect(() => {
        if (!user) return;
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const current = agencies.find((a) => a.id === agencyId);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            const r = await api.post("/agencies", {
                name: form.name.trim(),
                address: form.address.trim(),
                brand_concessionario: form.brand_concessionario || null,
            });
            // No default wallets seeded — operator creates them in Apertura saldi or via /wallets
            toast.success(`Agenzia "${r.data.name}" creata`, {
                description: "Configura subito l'apertura saldi (puoi anche saltare)",
                action: {
                    label: "Apri Apertura",
                    onClick: () => navigate("/settings/avanzate?onboarding=1"),
                },
                duration: 10000,
            });
            setForm({ name: "", address: "", brand_concessionario: null });
            setCreateOpen(false);
            setOpen(false);
            await load();
            selectAgency(r.data.id);
            navigate("/settings/avanzate?onboarding=1");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.delete(`/agencies/${deleteTarget.id}`);
            toast.success(`Agenzia "${deleteTarget.name}" eliminata`);
            const wasCurrent = deleteTarget.id === agencyId;
            setDeleteTarget(null);
            await load();
            const remaining = await api.get("/agencies");
            if (remaining.data.length === 0) {
                // Last agency wiped out → redirect to onboarding to start fresh
                selectAgency(null);
                toast.info("Nessuna agenzia rimasta. Riparti dall'onboarding.", {
                    duration: 6000,
                });
                navigate("/onboarding");
            } else if (wasCurrent) {
                selectAgency(remaining.data[0].id);
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setDeleting(false);
        }
    };

    if (agencies.length === 0) return null;

    const currentBrand = findConcessionario(current?.brand_concessionario);

    return (
        <div className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 px-3 py-2 border border-zinc-200 hover:border-ink transition-colors max-w-[220px]"
                data-testid="agency-switcher-button"
                title={current?.name || ""}
            >
                {currentBrand && currentBrand.glyph && (
                    <img
                        src={currentBrand.glyph}
                        alt={currentBrand.name}
                        className="w-7 h-7 flex-shrink-0"
                    />
                )}
                <div className="text-left min-w-0 flex-1">
                    <div className="overline leading-none">Agenzia</div>
                    <div className="text-sm font-bold mt-0.5 leading-tight truncate">
                        {current?.name || "Seleziona"}
                    </div>
                </div>
                <CaretDown size={14} weight="bold" className="flex-shrink-0" />
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-zinc-200 z-50">
                    {agencies.map((a) => {
                        const ab = findConcessionario(a.brand_concessionario);
                        return (
                        <div
                            key={a.id}
                            className={`flex items-stretch border-b border-zinc-100 hover:bg-zinc-50 ${a.id === agencyId ? "bg-zinc-50" : ""}`}
                        >
                            <button
                                onClick={() => {
                                    selectAgency(a.id);
                                    setOpen(false);
                                }}
                                className="flex-1 text-left px-4 py-3 flex items-center gap-3"
                                data-testid={`agency-option-${a.id}`}
                            >
                                {ab && ab.glyph && (
                                    <img
                                        src={ab.glyph}
                                        alt={ab.name}
                                        className="w-8 h-8 flex-shrink-0"
                                    />
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-bold truncate">{a.name}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5 truncate">
                                        {a.address || "—"}
                                    </div>
                                </div>
                            </button>
                            {canCreate && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteTarget(a);
                                        setOpen(false);
                                    }}
                                    className="px-3 text-zinc-400 hover:text-red-600 hover:bg-zinc-100"
                                    data-testid={`delete-agency-${a.id}`}
                                    aria-label="Elimina agenzia"
                                    title={agencies.length === 1
                                        ? "Elimina agenzia (azzera tutti i dati operativi)"
                                        : "Elimina agenzia"}
                                >
                                    <Trash size={13} weight="bold" />
                                </button>
                            )}
                        </div>
                        );
                    })}
                    {canCreate && (
                        <button
                            onClick={() => {
                                setCreateOpen(true);
                                setOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 text-sm font-bold flex items-center gap-2 hover:bg-ink hover:text-white transition-colors border-t border-zinc-200"
                            data-testid="new-agency-btn"
                        >
                            <Plus size={14} weight="bold" /> Nuova agenzia
                        </button>
                    )}
                </div>
            )}

            {createOpen && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setCreateOpen(false)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={submit}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                        data-testid="new-agency-form"
                    >
                        <div className="overline mb-3">Nuova agenzia</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-6">
                            Crea una nuova sede
                        </h2>
                        <p className="text-sm text-zinc-600 mb-6">
                            La nuova agenzia verrà creata con due wallet predefiniti ({" "}
                            <strong>Cassa</strong> e <strong>Banca</strong>) a saldo zero.
                            Potrai modificarli, aggiungere fornitori, clienti e categorie dopo
                            la creazione.
                        </p>
                        <label className="block mb-4">
                            <div className="overline mb-2">Nome agenzia *</div>
                            <input
                                autoFocus
                                className="inp"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                required
                                placeholder="Es. Sede Milano"
                                data-testid="new-agency-name"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Brand concessionario</div>
                            <BrandPicker
                                value={form.brand_concessionario}
                                onChange={(id) =>
                                    setForm({ ...form, brand_concessionario: id === "custom" ? null : id })
                                }
                                testId="new-agency-brand"
                            />
                            <div className="text-xs text-zinc-500 mt-1">
                                Opzionale. Cambialo dopo da Impostazioni → Agenzia.
                            </div>
                        </label>
                        <label className="block mb-8">
                            <div className="overline mb-2">Indirizzo</div>
                            <input
                                className="inp"
                                value={form.address}
                                onChange={(e) =>
                                    setForm({ ...form, address: e.target.value })
                                }
                                placeholder="Via, n., città"
                                data-testid="new-agency-address"
                            />
                        </label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setCreateOpen(false)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="btn-primary flex-1"
                                data-testid="new-agency-submit"
                            >
                                {saving ? "Creazione…" : "Crea"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {deleteTarget && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setDeleteTarget(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                        data-testid="delete-agency-modal"
                    >
                        <div className="overline mb-3 text-red-600">Elimina agenzia</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-6">
                            Elimina "{deleteTarget.name}"?
                        </h2>
                        <div className="p-3 bg-red-50 border border-red-200 text-xs text-red-800 mb-6">
                            <strong>Operazione IRREVERSIBILE.</strong> Verranno eliminati
                            tutti i dati di questa sede: wallet, fornitori, clienti, categorie,
                            transazioni, chiusure, dispositivi POS e log di audit. Gli operatori
                            collegati resteranno nell'azienda ma senza agenzia assegnata.
                            {agencies.length === 1 && (
                                <div className="mt-2 pt-2 border-t border-red-200">
                                    Questa è l'ultima agenzia: dopo l'eliminazione verrai
                                    riportato all'<strong>onboarding</strong> per ricreare una
                                    nuova sede da zero (l'account proprietario rimane attivo).
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="btn-destructive flex-1"
                                data-testid="delete-agency-confirm"
                            >
                                {deleting ? "Eliminazione…" : "Elimina tutto"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
